import { statementImportConfirmInputFingerprintV2, statementImportDiscardInputFingerprintV2,
  statementImportIntentInputFingerprintV2, statementImportReconciliationInputFingerprintV2,
  statementImportReviewInputFingerprintV2 } from "@tradvera/portfolio-domain";
import type { PortfolioStatementImportV2, StatementImportIntentV2,
  StatementImportReviewDecisionV2 } from "@tradvera/contracts";
import type { StatementImportClientV2 } from "./client";
import type { DurableStatementUploadStorageV2 } from "./durable-upload";
import { executeDurableStatementWriteV2 } from "./durable-write";

interface Scope {
  client: StatementImportClientV2; storage: DurableStatementUploadStorageV2;
  ownerId: string; bookId: string; imported: PortfolioStatementImportV2;
}

export function reviewStatementImportFromWorkspaceV2(scope: Scope,
  decisions: StatementImportReviewDecisionV2[], expectedRevision: number) {
  if (expectedRevision !== scope.imported.reviewRevision) invalid("STATEMENT_IMPORT_REVIEW_REQUIRED");
  const fieldIds = new Set([...scope.imported.fields, ...scope.imported.records.flatMap((record) => record.fields)]
    .map((field) => field.fieldId));
  if (!decisions.length || new Set(decisions.map((item) => item.fieldId)).size !== decisions.length
    || decisions.some((item) => !fieldIds.has(item.fieldId))) invalid("STATEMENT_IMPORT_REVIEW_REQUIRED");
  return executeDurableStatementWriteV2({ ...durableScope(scope), operation: "REVIEW",
    targetReviewRevision: expectedRevision + 1,
    buildInput: (idempotencyKey) => ({ schemaVersion: scope.imported.schemaVersion,
      importId: scope.imported.importId, expectedRevision, idempotencyKey, decisions }),
    inputFingerprint: statementImportReviewInputFingerprintV2,
    write: (input) => scope.client.review(input) });
}

export function reconcileStatementImportFromWorkspaceV2(scope: Scope) {
  if (scope.imported.state !== "RECONCILING") invalid("STATEMENT_IMPORT_RECONCILIATION_REQUIRED");
  return executeDurableStatementWriteV2({ ...durableScope(scope), operation: "RECONCILIATION",
    buildInput: (idempotencyKey) => ({ schemaVersion: scope.imported.schemaVersion,
      importId: scope.imported.importId, expectedRevision: scope.imported.reviewRevision, idempotencyKey }),
    inputFingerprint: statementImportReconciliationInputFingerprintV2,
    write: (input) => scope.client.reconcile(input) });
}

export function createStatementIntentFromWorkspaceV2(scope: Scope) {
  requireAcceptable(scope.imported, "READY_TO_CONFIRM");
  return executeDurableStatementWriteV2({ ...durableScope(scope), operation: "ACCEPT_INTENT",
    buildInput: (idempotencyKey) => ({ schemaVersion: scope.imported.schemaVersion,
      importId: scope.imported.importId, expectedBookVersion: scope.imported.expectedBookVersion,
      reconciliationFingerprint: scope.imported.reconciliation.inputFingerprint!, idempotencyKey,
      ownerReviewAcknowledged: true as const, duplicateReviewAcknowledged: true as const }),
    inputFingerprint: statementImportIntentInputFingerprintV2,
    write: (input) => scope.client.createIntent(input, scope.imported.reconciliation.dispositionCounts) });
}

export function confirmStatementImportFromWorkspaceV2(scope: Scope, intent: StatementImportIntentV2) {
  requireAcceptable(scope.imported, "INTENT_CREATED");
  if (intent.importId !== scope.imported.importId || intent.exactBookId !== scope.bookId
    || intent.financialAuthority !== "NONE"
    || !sameCounts(intent.dispositionCounts, scope.imported.reconciliation.dispositionCounts)) {
    invalid("STATEMENT_IMPORT_INTENT_MISMATCH");
  }
  return executeDurableStatementWriteV2({ ...durableScope(scope), operation: "ACCEPT_CONFIRM",
    buildInput: (idempotencyKey) => ({ schemaVersion: scope.imported.schemaVersion,
      importId: scope.imported.importId, intentId: intent.intentId, exactBookId: scope.bookId,
      challengeToken: intent.challengeToken, intentFingerprint: intent.inputFingerprint,
      idempotencyKey, ownerConfirmation: true as const, observationsOnlyAcknowledged: true as const }),
    inputFingerprint: statementImportConfirmInputFingerprintV2,
    write: (input) => scope.client.confirm(input, scope.imported.reconciliation.dispositionCounts) });
}

export function discardStatementImportFromWorkspaceV2(scope: Scope) {
  if (["OBSERVATIONS_ACCEPTED", "DUPLICATE_NOOP", "ABANDONED"].includes(scope.imported.state)) {
    invalid("STATEMENT_IMPORT_CONFIRMATION_REQUIRED");
  }
  return executeDurableStatementWriteV2({ ...durableScope(scope), operation: "DISCARD",
    buildInput: (idempotencyKey) => {
      const canonical = { schemaVersion: scope.imported.schemaVersion, importId: scope.imported.importId,
        bookId: scope.bookId, idempotencyKey, ownerConfirmation: true as const };
      return { ...canonical, inputFingerprint: statementImportDiscardInputFingerprintV2(canonical) };
    },
    inputFingerprint: (input) => input.inputFingerprint,
    write: (input) => scope.client.discard(input) });
}

function durableScope(scope: Scope) {
  return { storage: scope.storage, ownerId: scope.ownerId,
    bookId: scope.bookId, importId: scope.imported.importId };
}

function requireAcceptable(imported: PortfolioStatementImportV2,
  state: "READY_TO_CONFIRM" | "INTENT_CREATED"): void {
  const counts = imported.reconciliation.dispositionCounts;
  if (imported.state !== state || imported.financialAuthority !== "NONE"
    || imported.reconciliation.state !== "MATCHED" || !imported.reconciliation.inputFingerprint
    || imported.reconciliation.blockingCodes.length > 0
    || counts.POSSIBLE_DUPLICATE > 0 || counts.CONFLICT > 0) {
    invalid("STATEMENT_IMPORT_POSSIBLE_DUPLICATE");
  }
}

function invalid(code: string): never { throw new Error(code); }
function sameCounts(left: PortfolioStatementImportV2["reconciliation"]["dispositionCounts"],
  right: PortfolioStatementImportV2["reconciliation"]["dispositionCounts"]) {
  return (["NEW", "BIND_EXISTING", "SNAPSHOT_ONLY", "POSSIBLE_DUPLICATE", "CONFLICT", "IGNORE"] as const)
    .every((key) => left[key] === right[key]);
}

